import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useContext, useState, useEffect, useRef } from "react";
import { toast } from "sonner";

import IntialLoadingPage from "@/common/components/loaders/IntialLoadingPage";
import {
  useFetchUserDetails,
  useFetchUserPreference,
  useFetchUserSession,
  useLogin,
  useCheckUserExist,
  useValidateBusinessEmail,
  useCheckWebaddressAvail,
  useCheckCompanyNameAvail,
  useCreateUserAccount,
  useResetPassword,
  useFetchWorkspaceDetails,
  useLogoutUser,
} from "@/common/services/users";
import { clearOrgSelectedForSession } from "@/common/utils/orgSession";

const AUTH_BOOTSTRAP_HINT_KEY = "apqa.authBootstrapHint";

function readAuthBootstrapHint(): boolean {
  try {
    return window.sessionStorage.getItem(AUTH_BOOTSTRAP_HINT_KEY) === "1";
  } catch {
    return false;
  }
}

function writeAuthBootstrapHint(isAuthenticated: boolean): void {
  try {
    if (isAuthenticated) {
      window.sessionStorage.setItem(AUTH_BOOTSTRAP_HINT_KEY, "1");
    } else {
      window.sessionStorage.removeItem(AUTH_BOOTSTRAP_HINT_KEY);
    }
  } catch {
    // sessionStorage unavailable — non-critical
  }
}

interface User {
  id?: string;
  name?: string;
  email?: string;
  username?: string;
  password: string;
  firstName?: string;
  lastName?: string;
  companyname?: string;
}

interface AuthContextType {
  user: User | null;
  login: (userData: User) => Promise<boolean>;
  logoutUser: () => void;
  checkUserExist: (email: string) => Promise<any>;
  validateBusinessEmail: (email: string) => Promise<any>;
  validateWebaddress: (webaddress: string) => Promise<any>;
  checkCompanyNameAvail: (name: string) => Promise<boolean>;
  createUserAccount: (userData: object) => Promise<any>;
  resetPassword: (email: string) => Promise<any>;
  refetchUserPreference: () => void;
  isAuthenticated: boolean | null;
  isAppReady: boolean;
  /** True while the initial session is being validated against the server using
   *  the sessionStorage bootstrap hint. Routes must not make redirect decisions
   *  (e.g. org-gate, onboarding guard) until this is false. */
  isBootstrappingFromHint: boolean;
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  isUserDetailsLoading: boolean;
  isUserPreferenceLoading: boolean;
  userPreferenceData: any;
  userPreference: any | null;
  useWorkspaceVersions: any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const initialAuthHint = readAuthBootstrapHint();
  const [user, setUser] = useState<User | null>(null);
  const [userPreference, setUserPreference] = useState<any | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(
    initialAuthHint ? true : null,
  );
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [isOrgAdmin, setIsOrgAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isBootstrappingFromHint, setIsBootstrappingFromHint] =
    useState(initialAuthHint);
  const { mutateAsync: logoutMutate } = useLogoutUser();
  const queryClient = useQueryClient();
  const { data: userSessionData, isError, isLoading } = useFetchUserSession();
  const {
    data: userDetailsData,
    isLoading: isUserDetailsLoading,
    isError: isUserDetailsError,
    error: userDetailsError,
    refetch: refetchUserDetails,
  } = useFetchUserDetails({
    enabled: !!userSessionData?.data?.user,
  });
  const {
    data: userPreferenceData,
    isLoading: isUserPreferenceLoading,
    refetch: refetchUserPreference,
  } = useFetchUserPreference({
    enabled: !!userSessionData?.data?.user,
  });
  const {
    data: useWorkspaceVersions,
    isLoading: isWorkspaceDetailsLoading,
    isError: isWorkspaceDetailsError,
  } = useFetchWorkspaceDetails(userPreferenceData?.data?.versionId, {
    enabled: !!userPreferenceData?.data?.versionId,
  });

  const hasBootstrappedWorkspace = useRef(false);
  if (useWorkspaceVersions) {
    hasBootstrappedWorkspace.current = true;
  }
  const { mutateAsync: loginMutate } = useLogin();
  const { mutateAsync: resetPasswordMutate } = useResetPassword();
  const { mutateAsync: checkUserExistMutate } = useCheckUserExist();
  const { mutateAsync: validateBusinessEmailMutate } =
    useValidateBusinessEmail();
  const { mutateAsync: validateWebaddressMutate } = useCheckWebaddressAvail();
  const { mutateAsync: checkCompanyNameMutate } = useCheckCompanyNameAvail();
  const { mutateAsync: createUserAccountMutate } = useCreateUserAccount();

  // Cross-tab logout: listen for logout broadcast from other tabs
  useEffect(() => {
    const channel = new BroadcastChannel("auth");
    channel.onmessage = (e) => {
      if (e.data === "logout") {
        toast.dismiss();
        writeAuthBootstrapHint(false);
        setIsBootstrappingFromHint(false);
        setIsAuthenticated(false);
        setUser(null);
        setUserPreference(null);
        window.location.replace("/login");
      }
    };
    return () => channel.close();
  }, []);

  useEffect(() => {
    // If userSessionData is still loading or has failed, keep the loading state
    if (isLoading) {
      return; // If still loading, do nothing
    }

    if (userSessionData) {
      // If session data exists, set authentication state
      if (userSessionData?.data?.user) {
        writeAuthBootstrapHint(true);
        setIsAuthenticated(true);
      } else {
        writeAuthBootstrapHint(false);
        setIsAuthenticated(false);
      }
    } else if (isError) {
      // Handle error scenario
      console.error("Failed to fetch user session data");
      writeAuthBootstrapHint(false);
      setIsAuthenticated(false); // You can set it to false or handle it as per your requirement
    }

    setIsBootstrappingFromHint(false);
    setLoading(false); // Set loading to false after processing the session data (either success or error)
  }, [userSessionData, isLoading, isError]);

  useEffect(() => {
    const userNotFoundMessage = String(
      (userDetailsError as any)?.response?.data?.error ||
        (userDetailsError as any)?.response?.data?.message ||
        (userDetailsError as any)?.message ||
        "",
    );
    const isBrokenUserSession =
      isUserDetailsError &&
      /user\s*not\s*found|not authenticated/i.test(userNotFoundMessage);

    if (isBrokenUserSession) {
      writeAuthBootstrapHint(false);
      setIsBootstrappingFromHint(false);
      setIsAuthenticated(false);
      setUser(null);
      setUserPreference(null);
      clearOrgSelectedForSession();
      queryClient.clear();
      window.location.replace("/login");
      return;
    }

    if (userDetailsData?.data) {
      setUser(userDetailsData.data);
      setIsSuperAdmin(userDetailsData.data?.isSuperAdmin === true);
      const role = userDetailsData.data?.membershipRole;
      const roleName =
        (userDetailsData.data as any)?.roleName ||
        (userDetailsData.data as any)?.orgRoleName ||
        (userDetailsData.data as any)?.role ||
        (userDetailsData.data as any)?.userrole?.name;
      const normalized = String(roleName || "")
        .toLowerCase()
        .trim();
      const isTM =
        normalized === "test manager" ||
        normalized === "org admin" ||
        normalized === "organization admin";
      setIsOrgAdmin(role === "owner" || role === "admin" || isTM);
    }
  }, [userDetailsData, isUserDetailsError, userDetailsError, queryClient]);

  useEffect(() => {
    if (userPreferenceData?.data) {
      setUserPreference(userPreferenceData.data);
      const prefRole =
        userPreferenceData.data?.roleName ||
        (userPreferenceData.data as any)?.role ||
        (userPreferenceData.data as any)?.userrole?.name;
      const normalized = String(prefRole || "")
        .toLowerCase()
        .trim();
      if (normalized === "test manager" || normalized === "org admin") {
        setIsOrgAdmin(true);
      }
    }
  }, [userPreferenceData]);

  const login = async (userData: User): Promise<boolean> => {
    try {
      const response = await loginMutate(userData);
      if (response) {
        // Refetch user details and preference immediately after successful login
        await Promise.all([refetchUserDetails(), refetchUserPreference()]);
        writeAuthBootstrapHint(true);
        setIsBootstrappingFromHint(false);
        setIsAuthenticated(true);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error during login:", error);
      throw error;
    }
  };

  const resetPassword = async (email: string): Promise<boolean> => {
    try {
      const response = await resetPasswordMutate(email);
      if (response) {
        return true;
      } else {
        return false;
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send reset email";
      throw new Error(message);
    }
  };

  const logoutUser = async () => {
    toast.dismiss();
    // Do NOT touch isAuthenticated or clear sessionStorage until AFTER the server
    // destroys the session cookie. If we flip isAuthenticated=false first, React
    // re-renders ProtectedRoute while the cookie is still valid — useFetchUserSession
    // refetches and gets 200, sets isAuthenticated=true again, org gate fires with
    // hasSelectedOrgThisSession()=false (just cleared) → redirects to /select-org.
    try {
      await logoutMutate(undefined);
    } catch {
      /* best-effort — proceed with client-side cleanup regardless */
    } finally {
      writeAuthBootstrapHint(false);
      setIsBootstrappingFromHint(false);
      setIsAuthenticated(false);
      setUser(null);
      setUserPreference(null);
      setLoading(false);
      clearOrgSelectedForSession();
      queryClient.clear();
      const channel = new BroadcastChannel("auth");
      channel.postMessage("logout");
      channel.close();
      // Hard-navigate — fresh page load hits server with no valid cookie → 401 → stays on login.
      window.location.replace("/login");
    }
  };

  const checkUserExist = async (email: string): Promise<any> => {
    try {
      const response = await checkUserExistMutate(email);
      // Backend returns true = email available (not registered), false = taken
      return response?.data === false;
    } catch (error) {
      console.error("Error checking user existence:", error);
      return false;
    }
  };

  const validateBusinessEmail = async (email: string): Promise<any> => {
    try {
      const response = await validateBusinessEmailMutate(email);
      if (response?.data) {
        return true; // Email is valid
      } else {
        return false; // Email is invalid
      }
    } catch (error) {
      console.error("Error validating business email:", error);
      return false;
    }
  };

  const validateWebaddress = async (webaddress: string): Promise<any> => {
    try {
      const response = await validateWebaddressMutate(webaddress);
      if (response?.data) {
        return true; // Webaddress is valid
      } else {
        return false; // Webaddress is invalid
      }
    } catch (error) {
      console.error("Error validating web address:", error);
      return false;
    }
  };

  const checkCompanyNameAvail = async (name: string): Promise<boolean> => {
    try {
      const response = await checkCompanyNameMutate(name);
      // Backend returns `true` if available, `false` if taken
      return response?.data === true;
    } catch {
      return true; // fail open — don't block signup on network error
    }
  };

  const createUserAccount = async (userData: object): Promise<any> => {
    try {
      const response = await createUserAccountMutate(userData);
      return response;
    } catch (error) {
      console.error("Error validating web address:", error);
      return false;
    }
  };

  // Only block the full page for session check (need to know if authenticated for routing)
  // Skip the full-screen loader on refresh when this browser session already proved
  // auth once — keep the shell mounted and let route/content loaders handle the rest.
  const isSessionLoading =
    !isBootstrappingFromHint &&
    isAuthenticated !== false &&
    (isLoading || loading);
  if (isSessionLoading) {
    return <IntialLoadingPage />;
  }

  // App shell can render while preferences/workspace load — content area will show a spinner.
  // Must wait for userPreferenceData so that _activeProjectId (X-Project-Id header) is set
  // before page queries fire.
  const isAppReady =
    !isAuthenticated ||
    isSuperAdmin ||
    (!!userPreferenceData?.data &&
      (!userPreferenceData?.data?.versionId ||
        hasBootstrappedWorkspace.current));

  return (
    <AuthContext.Provider
      value={{
        user,
        userPreference,
        isAuthenticated,
        isAppReady,
        isBootstrappingFromHint,
        isSuperAdmin,
        isOrgAdmin,
        isUserDetailsLoading,
        isUserPreferenceLoading,
        userPreferenceData,
        useWorkspaceVersions,
        login,
        resetPassword,
        logoutUser,
        checkUserExist,
        validateBusinessEmail,
        validateWebaddress,
        checkCompanyNameAvail,
        createUserAccount,
        refetchUserPreference,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
